<template>
  <div>
    <h1>Take the J.E.D.I. pledge</h1>

    <p class="mt-3 font-sans-book text-lg" style="line-height: 1.75rem">
      Reimagine is committed to uplifting diverse voices and actively advancing Justice, Equity, Diversity, and Inclusion (J.E.D.I.) throughout the world. To help us improve and to make sure we are fostering a safe, inclusive community, we appreciate you answering the following questions and taking Reimagine’s J.E.D.I. pledge.
    </p>

    <div class="spacer" />

    <p class="mt-3 font-sans-book text-lg" style="line-height: 1.75rem">
      If you self-identify as an individual from an underrepresented community (person of color, person with disabilities, LGBTQ+, etc.)
      or you plan to center the experiences of underrepresented communities in your event,
      you may be eligible for in-kind Facebook ads to help with promotion: $100 each to 25 Reimagine event hosts.
      Simply contact our <a href="mailto:margaret@letsreimagine.org">Marketing Manager</a> if you have questions or wish to nominate someone.
    </p>

    <div class="spacer" />

    <div>
      <label>
        What ethnicity do you identify with? (optional)
        <span class="block text-sm font-sans-book">
          Choose as many as you like
        </span>
      </label>
      <div class="group__checkbox mt-2">
        <div class="flex flex-wrap select-box">
          <template v-for="option in options.ethnicities">
            <label class="w-full text-base font-normal md:w-1/2 md:pr-3" :key="option.id">
              <input type="checkbox" v-model="form.ethnicities" :value="option.id"/>
              <span class="inline-block mr-2 check"></span> {{ option.name }}
            </label>
          </template>
        </div>
      </div>
    </div>

    <div class="spacer" />

    <div>
      <label>
        What pronouns do you identify with? (optional)
        <span class="block text-sm font-sans-book">
          Choose as many as you like
        </span>
      </label>
      <div class="group__checkbox mt-2">
        <div class="flex flex-wrap select-box">
          <template v-for="option in options.pronouns">
            <label class="w-full text-base font-normal md:w-1/2 md:pr-3" :key="option.id">
              <input type="checkbox" v-model="form.pronouns" :value="option.id"/>
              <span class="inline-block mr-2 check"></span> {{ option.name }}
            </label>
          </template>
        </div>
      </div>

    </div>

    <div class="spacer" />

    <div>
      <label>
        What gender identity do you identify with? (optional)
        <span class="block text-sm font-sans-book">
          Choose as many as you like
        </span>
      </label>
      <div class="group__checkbox mt-2">
        <div class="flex flex-wrap select-box">
          <template v-for="option in options.genders">
            <label class="w-full text-base font-normal md:w-1/2 md:pr-3" :key="option.id">
              <input type="checkbox" v-model="form.genders" :value="option.id"/>
              <span class="inline-block mr-2 check"></span> {{ option.name }}
            </label>
          </template>
        </div>
      </div>
    </div>

    <div class="spacer" />

    <div>
      <label>
        What is your sexual orientation? (optional)
        <span class="block text-sm font-sans-book">
          Choose as many as you like
        </span>
      </label>
      <div class="group__checkbox mt-2">
        <div class="flex flex-wrap select-box">
          <template v-for="option in options.sexualities">
            <label class="w-full text-base font-normal md:w-1/2 md:pr-3" :key="option.id">
              <input type="checkbox" v-model="form.sexualities" :value="option.id"/>
              <span class="inline-block mr-2 check"></span> {{ option.name }}
            </label>
          </template>
        </div>
      </div>
    </div>

    <div class="spacer" />

    <hr class="w-px bg-n-gray border border-n-gray w-full" />

    <div class="spacer" />

    <h1 class="mb-5">Shared commitment&nbsp;<span class="text-red">*</span></h1>

    <label>
      Sign the Reimagine J.E.D.I. Community Pledge
    </label>

    <JediCommunityPledge />

    <div class="select-box mt-4">
      <label class="w-full text-base font-normal">
        <input name="community_pledge" v-validate="'required'" type="checkbox" v-model="form.community_pledge" />
        <span class="inline-block mr-2 check" />
        <span class="font-normal font-sans-book">
          I agree to the J.E.D.I. Community Pledge
        </span>
      </label>
    </div>
    <div v-show="errors.has('community_pledge')" class="block error-message">
      {{ (errors.first('community_pledge') || '').replace('_', ' ') }}
    </div>

    <div class="spacer" />

    <hr class="w-px bg-n-gray border border-n-gray w-full" />

    <div class="spacer" />

    <div>
      Required actions to cultivate JEDI (Justice, Equity Diversity, and Inclusion):
      I agree to attend live or watch a recording of The Art of Creating Inclusive Spaces,
      a 90-minute training developed in partnership with our collaborators
      at <NewTabA11y href="https://www.holisticunderground.org/">Holistic Underground</NewTabA11y>,
      a non-profit that supports projects which empower change makers, leaders and organizations in social and environmental impact.
      The training and workshop materials can be found <NewTabA11y href="https://letsreimagine.org/creating-inclusive-spaces">here</NewTabA11y>.
      Or, I agree to attend another formal training program to increase my skill and capacity for creating space through an equity and inclusion lens.
    </div>

    <div class="spacer" />

    <div>
      <div class="flex flex-wrap select-box">
        <label class="w-full text-base font-normal md:w-1/2 md:pr-3 pb-2 md:pb-0">
          <input v-model="form.jedi_training" v-validate="'required'" name="jedi_training" type="radio" value="will_watch">
          <span class="inline-block mr-2 radio" /> I agree to watch the training
        </label>
        <label class="w-full text-base font-normal md:w-1/2 md:pr-3">
          <input v-model="form.jedi_training" name="jedi_training" type="radio" value="already_trained">
          <span class="inline-block mr-2 radio" /> I have already attended a similiar training
        </label>
      </div>
      <div v-show="errors.has('jedi_training')" class="block error-message">
        {{ (errors.first('jedi_training') || '').replace('_', ' ') }}
      </div>

      <div class="mt-2" v-if="form.jedi_training === 'already_trained'">
        <label>
          What is the name of the training you have attended?
        </label>
        <input id="jedi_training_received" v-model="form.jedi_training_received" v-validate="'required|max:60'" name="jedi_training_received" type="text">
        <div v-show="errors.has('jedi_training_received')" class="block error-message">
          {{ (errors.first('jedi_training_received') || '').replaceAll('_', ' ') }}
        </div>
      </div>
    </div>

    <div class="spacer" />

    <hr class="w-px bg-n-gray border border-n-gray w-full" />

    <div class="spacer" />

    <div class="flex justify-center">
      <button @click="finish" class="button">finish</button>
    </div>

    <div class="spacer" />
  </div>
</template>

<script>
import { optionsAPI } from "../../../api/optionsAPI";
import JediCommunityPledge from "../components/jedi/JediCommunityPledge.vue";
import {mapGetters} from "vuex";
import {authAPI} from "../../../api/authAPI";
import NewTabA11y from "../../global/a11y/NewTabA11y.vue";
import {analyticsDataLayer} from "../../../api/analyticsDataLayer";

export default {
  name: "JediPledge",
  components: {NewTabA11y, JediCommunityPledge},
  data() {
    return {
      options: {
        ethnicities: [],
        pronouns: [],
        genders: [],
        sexualities: [],
      },
      form: {
        ethnicities: [],
        pronouns: [],
        genders: [],
        sexualities: [],
        community_pledge: false,
        jedi_training: null,
        jedi_training_received: ''

      }
    }
  },
  computed: {
    ...mapGetters({
      user: "user",
    })
  },
  async created() {
    const res = await optionsAPI.byGroups(['ethnicities', 'pronouns', 'genders', 'sexualities']);
    this.options.ethnicities = res.ethnicities;
    this.options.pronouns = res.pronouns;
    this.options.genders = res.genders;
    this.options.sexualities = res.sexualities;
  },
  methods: {
    async finish() {
      const hasNoFormErrors = await this.$validator.validateAll();

      if (!hasNoFormErrors) {
        // return;
      }

      const response = await authAPI.register.jediPledgeAgreement(this.form);

      if (response.success) {
        await this.$store.dispatch("getMe");
        analyticsDataLayer.emit('register_done');

        if (this.user.isAdmin) {
          window.location.href = "/events";
        } else {
          window.location.href = "/";
        }

        return;
      }

      Object.keys(response.errors).forEach(formAttr => {
        const errorMessage = response.errors[formAttr].find(() => true);
        this.errors.add({ field: formAttr, msg: errorMessage });
      });
    }
  }
}
</script>

<style scoped>

@media only screen and (min-width: 0) and (max-width: 410px) {
  .hider-vid-spacer {
    min-height: 0 !important;
  }
}

textarea {
  -webkit-text-fill-color: #4a4a4a;
  opacity: 1;
}
</style>
